/**
 * Pure helpers for the markdown panel: component block ranges, "Add component" lock logic.
 */

const BLOCK_OPEN_REGEX = /^:::\s+\w+/;
const BLOCK_CLOSE_REGEX = /^:::\s*$/;
const FENCED_ARTICLE_HERO_OPEN_REGEX = /^```\s*ArticleHero\s*$/i;
const FENCED_BLOCK_CLOSE_REGEX = /^```\s*$/;

/** Replace unusual line terminators (LS U+2028, PS U+2029) with normal newline. */
export function normalizeLineTerminators(s: string): string {
	return s.replace(/\u2028/g, "\n").replace(/\u2029/g, "\n");
}

/**
 * Returns inclusive [startLine, endLine] for each component block (::: Type ... :::).
 * Opening line, body, and closing line are all part of the block.
 */
export function getComponentBlockRanges(lines: string[]): Array<[number, number]> {
	const ranges: Array<[number, number]> = [];
	let blockStart: number | null = null;
	let inFencedBlock = false;
	for (let i = 0; i < lines.length; i++) {
		const lineNum = i + 1;
		const trimmed = (lines[i] ?? "").trim();
		if (FENCED_ARTICLE_HERO_OPEN_REGEX.test(trimmed)) {
			if (blockStart === null) {
				blockStart = lineNum;
				inFencedBlock = true;
			}
		} else if (inFencedBlock && FENCED_BLOCK_CLOSE_REGEX.test(trimmed) && blockStart !== null) {
			ranges.push([blockStart, lineNum]);
			blockStart = null;
			inFencedBlock = false;
		} else if (!inFencedBlock && BLOCK_OPEN_REGEX.test(trimmed)) {
			if (blockStart === null) blockStart = lineNum;
		} else if (!inFencedBlock && BLOCK_CLOSE_REGEX.test(trimmed) && blockStart !== null) {
			ranges.push([blockStart, lineNum]);
			blockStart = null;
		}
	}
	if (blockStart !== null) ranges.push([blockStart, lines.length]);
	return ranges;
}

/** True if cursor is on any line that belongs to a component block (opening, body, or closing). */
export function isCursorInsideAnyComponentBlock(lines: string[], cursorLine1Based: number): boolean {
	const ranges = getComponentBlockRanges(lines);
	return ranges.some(([start, end]) => cursorLine1Based >= start && cursorLine1Based <= end);
}

/** True if the line is a title (# or ##) or has text content (so we should not replace it with a component). */
export function isLineTitleOrText(line: string): boolean {
	const trimmed = line.trim();
	if (!trimmed) return false;
	if (/^#+\s/.test(trimmed)) return true;
	if (BLOCK_OPEN_REGEX.test(trimmed) || BLOCK_CLOSE_REGEX.test(trimmed)) return false;
	if (FENCED_ARTICLE_HERO_OPEN_REGEX.test(trimmed) || FENCED_BLOCK_CLOSE_REGEX.test(trimmed)) return false;
	return true;
}
